/**
 * THE HELP PAGE REGISTRY — where the person is (owner request 2026-10-17; design §31.9.4, OD-5; ADR 0065, proposed).
 *
 * The route registry (`./route-registry.ts`) lists the screens the help assistant may OPEN; it leaves out every page that
 * names a record (an agent, the new-agent wizard of one agent_template, a campaign), because a proposal can never name a
 * record. This registry lists every page the person can BE ON — one entry per route of `apps/web-app/src/router.tsx`
 * (`page-registry.test.ts` fails when a route has none) — so the help host can tell the model "New agent, step Content"
 * instead of nothing. Owner report 2026-10-17: on the wizard's Content step the assistant said "Open Templates" because
 * the page context named no screen it knew.
 *
 * - The key is the page id the portal sends as `page_context.route_id`: the route registry's id for a registered screen
 *   (the same id, so a navigation target and the page agree), else an id of its own (`agent_setup`, `agent`).
 * - `path` is the router path, verbatim (a record parameter included: a page is matched, never opened, by it).
 * - `place` lists a page's steps or tabs by code; each has a move in the route registry (`<page id>.<code>`), offered
 *   only while the person is on this page. Names come from the move's entry.
 * - `state` lists the codes the page may report as empty or filled — never a value.
 * - `topic` picks the widget's suggested questions (`packages/help-widget`); it is never a route id.
 *
 * The public site: {@link helpPublicPageOf} names a wetalk.io page from its path, only for the pages listed; any other
 * path is "a page of wetalk.io" — a path is never shown to the model (a visitor can type anything into one).
 *
 * A reviewed file, beside the route and form registries (plan-019 GATE's `help-registry` rule reads all three).
 */
import { HELP_ROUTE_REGISTRY, helpPlaceRouteIdOf } from "./route-registry.js";
/** What the widget's suggested questions are about. */
export const HELP_PAGE_TOPIC = ["setup", "operate", "phone", "chat", "money", "outbound", "account", "way_in", "general", "price", "reading"];
function registered(id, topic) {
    const route = HELP_ROUTE_REGISTRY[id];
    return { path: route.path, screenName: route.screenName, placeKind: null, place: [], state: [], topic };
}
function own(path, screenName, topic) {
    return { path, screenName, placeKind: null, place: [], state: [], topic };
}
export const HELP_PAGE_REGISTRY = {
    // ── screens the assistant may also open (the route registry's ids) ──
    "sign_up": registered("sign_up", "way_in"),
    "log_in": registered("log_in", "way_in"),
    "dashboard": registered("dashboard", "general"),
    "agents": registered("agents", "operate"),
    "agents.new": registered("agents.new", "setup"),
    "live_activity": registered("live_activity", "operate"),
    "campaigns.new": registered("campaigns.new", "outbound"),
    "contact_lists": registered("contact_lists", "outbound"),
    "numbers": registered("numbers", "phone"),
    "messaging": registered("messaging", "chat"),
    "messaging.web_chat": registered("messaging.web_chat", "chat"),
    "settings": registered("settings", "account"),
    "compliance": registered("compliance", "account"),
    "do_not_call": registered("do_not_call", "outbound"),
    "test_console": registered("test_console", "operate"),
    "docs": registered("docs", "general"),
    "billing": registered("billing", "money"),
    "billing.ledger": registered("billing.ledger", "money"),
    "organization": registered("organization", "account"),
    "organization.workspaces": registered("organization.workspaces", "account"),
    "organization.people": registered("organization.people", "account"),
    "organization.security": registered("organization.security", "account"),
    "organization.sharing": registered("organization.sharing", "account"),
    "organization.archive_close": registered("organization.archive_close", "account"),
    "audit": registered("audit", "account"),
    "finance.funding": registered("finance.funding", "money"),
    "finance.plans": registered("finance.plans", "money"),
    "finance.allowances": registered("finance.allowances", "money"),
    "finance.documents": registered("finance.documents", "money"),
    "finance.postpaid": registered("finance.postpaid", "money"),
    "me.security": registered("me.security", "account"),
    "start.organization": registered("start.organization", "account"),
    // ── pages the assistant can name but never open (a record, a one-off landing) ──
    "agent_setup": {
        path: "/agents/new/$agentTemplateCode",
        screenName: { en: "New agent", el: "Νέος πράκτορας" },
        placeKind: "step",
        place: ["content", "menu", "hours", "orders", "language", "medium", "summary"],
        // The Content step's fields by kind, and whether the step shown can be left with Continue.
        state: ["typed_information", "document", "website_address", "opening_hours", "choice", "step_complete"],
        topic: "setup",
    },
    "agent": {
        path: "/agents/$voiceAgentId",
        screenName: { en: "Agent", el: "Πράκτορας" },
        placeKind: "tab",
        place: ["live", "orders", "review", "outbound", "results", "history", "analytics", "test", "channels", "versions"],
        state: [],
        topic: "operate",
    },
    "join": own("/join", { en: "Join your team", el: "Συμμετοχή στην ομάδα σας" }, "way_in"),
    "invitation": own("/invitation", { en: "Accept an invitation", el: "Αποδοχή πρόσκλησης" }, "way_in"),
    "help_fallback": own("/help", { en: "Help", el: "Βοήθεια" }, "general"),
    "first_run": own("/first-run", { en: "Welcome", el: "Καλώς ήρθατε" }, "setup"),
    "building": own("/building/$buildJobId", { en: "Building", el: "Δημιουργία" }, "setup"),
    "messaging.medium": own("/messaging/$mediumCode", { en: "Channel", el: "Κανάλι" }, "chat"),
    "payouts": own("/payouts", { en: "Payouts", el: "Αποδόσεις" }, "money"),
    "workspace": own("/workspaces/$workspaceId", { en: "Workspace", el: "Χώρος εργασίας" }, "account"),
    "customer_order": own("/orders/$customerOrderReference", { en: "Order", el: "Παραγγελία" }, "operate"),
    "contact_list": own("/contact-lists/$contactListId", { en: "Contact list", el: "Λίστα επαφών" }, "outbound"),
    "campaign": own("/campaigns/$campaignId", { en: "Campaign", el: "Καμπάνια" }, "outbound"),
    "campaign.edit": own("/campaigns/$campaignId/edit", { en: "Edit campaign", el: "Επεξεργασία καμπάνιας" }, "outbound"),
    "campaign.results": own("/campaigns/$campaignId/results", { en: "Survey results", el: "Αποτελέσματα έρευνας" }, "outbound"),
    "docs.article": own("/docs/$docsArticleId", { en: "Documentation", el: "Τεκμηρίωση" }, "general"),
};
/**
 * Router paths that are deliberately not pages: each only redirects. A reviewed list (`page-registry.test.ts` reads
 * it beside `router.tsx`).
 */
export const HELP_PAGE_REDIRECT_PATH = Object.freeze(["/", "/workspaces"]);
const HELP_PAGE_ID_SET = new Set(Object.keys(HELP_PAGE_REGISTRY));
export function isHelpPageId(value) {
    return typeof value === "string" && HELP_PAGE_ID_SET.has(value);
}
export function helpPageOf(value) {
    return isHelpPageId(value) ? HELP_PAGE_REGISTRY[value] : null;
}
const HELP_PAGE_ID_BY_PATH = new Map(Object.keys(HELP_PAGE_REGISTRY).map((id) => [HELP_PAGE_REGISTRY[id].path, id]));
/**
 * The page a portal location shows: the registered screen when the pathname is one (`/messaging/web_chat`), else the
 * page whose router path is the matched leaf route's (`/agents/$voiceAgentId`), else `null`.
 */
export function helpPageIdOfLocation(pathname, routePath) {
    const exact = HELP_PAGE_ID_BY_PATH.get(pathname) ?? HELP_PAGE_ID_BY_PATH.get(pathname.length > 1 ? pathname.replace(/\/$/u, "") : pathname);
    if (exact !== undefined)
        return exact;
    return HELP_PAGE_ID_BY_PATH.get(routePath) ?? null;
}
/** A place of a page, its name and its move: `null` unless both are registered. */
export function helpPagePlaceOf(pageId, place) {
    const page = helpPageOf(pageId);
    if (page === null || typeof place !== "string" || !page.place.includes(place))
        return null;
    const routeId = helpPlaceRouteIdOf(pageId, place);
    return routeId === null ? null : { routeId, name: HELP_ROUTE_REGISTRY[routeId].screenName };
}
/** The wetalk.io pages the assistant can name, by their path without the `/el` prefix. A prefix entry ends in `*`. */
export const HELP_PUBLIC_PAGE = Object.freeze({
    "/": { screenName: { en: "Home page", el: "Αρχική σελίδα" }, topic: "general" },
    "/pricing/": { screenName: { en: "Pricing", el: "Τιμές" }, topic: "price" },
    "/docs/api/": { screenName: { en: "API reference", el: "Αναφορά API" }, topic: "reading" },
    "/docs/*": { screenName: { en: "Documentation", el: "Τεκμηρίωση" }, topic: "reading" },
    "/status/*": { screenName: { en: "Status", el: "Κατάσταση" }, topic: "general" },
    "/help/": { screenName: { en: "Help", el: "Βοήθεια" }, topic: "general" },
    "/privacy/": { screenName: { en: "Privacy policy", el: "Πολιτική απορρήτου" }, topic: "general" },
    "/terms/": { screenName: { en: "Terms", el: "Όροι" }, topic: "general" },
});
/** The wetalk.io page a path shows, or `null` for any path not listed (never the path itself). */
export function helpPublicPageOf(pagePath) {
    if (typeof pagePath !== "string" || !pagePath.startsWith("/"))
        return null;
    let path = pagePath.split(/[?#]/u, 1)[0];
    if (path === "/el" || path.startsWith("/el/"))
        path = path.slice(3) === "" ? "/" : path.slice(3);
    if (!path.endsWith("/"))
        path = `${path}/`;
    const exact = HELP_PUBLIC_PAGE[path];
    if (exact !== undefined)
        return exact;
    for (const [key, spec] of Object.entries(HELP_PUBLIC_PAGE)) {
        if (key.endsWith("*") && path.startsWith(key.slice(0, -1)))
            return spec;
    }
    return null;
}
//# sourceMappingURL=page-registry.js.map